import { Instance, applySnapshot, flow, getParent, getSnapshot, types } from 'mobx-state-tree';

import { IReadChatRules, readChatRules } from '@common/api/chat-rule-acceptances';
import { postMedia } from '@common/api/media';
import { IUploadAvatarBody, getProfile, postAvatar } from '@common/api/profile';
import {
	IChangeUserPassword,
	UserActionTypes,
	changeUserLanguage,
	changeUserPassword,
	commitAction,
} from '@common/api/users';
import { IStoreInterfaceModel } from '@common/models/StoreInterface';

import { SERVICES } from '@common/constants/Services';
import { TAGS } from '@common/constants/Tags';
import { checkRole } from '@common/components/RBAC';
import BusinessUnit from '../Businesses/BusinessUnit';
import CgSchool from '../CgSchools/CgSchool';
import ConsultationProfileModel from '../Consultations/ConsultationProfile';
import UserOnboardingItemResult from '../Onboarding/UserOnboardingItemResult';
import Role from '../Roles/Role';
import Service from '../Services/Service';
import Admin from '../Users/Admins/Admin';
import CgCounselor from '../Users/CgCounselors/CgCounselor';
import CgCoordinator from '../Users/CgCoordinators/CgCoordinator';
import Mentor from '../Users/Mentors/Mentor';
import Parent from '../Users/Parents/Parent';
import Student from '../Users/Students/Student';
import Teacher from '../Users/Teachers/Teacher';

const ActionModel = types.model('Action', { action: types.maybeNull(types.string) });

const TeacherCertificate = types.model('TeacherCertificate', {
	url: types.maybeNull(types.string),
	png_url: types.maybeNull(types.string),
});

const Partner = types.model('Partner', {
	id: types.maybeNull(types.number),
	is_premium: types.maybeNull(types.boolean),
	phone: types.optional(types.maybeNull(types.string), ''),
	has_downloaded_certificate: types.maybeNull(types.boolean),
	teacher_certificate: types.maybeNull(TeacherCertificate),
	is_contract_signed: types.maybeNull(types.boolean),
});

const ProfileCgCoordinator = types.compose(
	CgCoordinator,
	types.model('ProfileCgCoordinator', {
		c_g_school_id: types.maybeNull(types.number),
		school: types.maybeNull(CgSchool),
		onboardingQuizzes: types.maybeNull(types.array(UserOnboardingItemResult)),
	}),
);

const ProfileModel = types
	.model('ProfileModel', {
		// TODO change to identifier type
		id: types.maybeNull(types.number),
		actions: types.optional(types.array(ActionModel), []),
		user_id: types.maybeNull(types.number),
		first_name: types.optional(types.maybeNull(types.string), ''),
		first_name_ua: types.optional(types.maybeNull(types.string), ''),
		last_name: types.optional(types.maybeNull(types.string), ''),
		last_name_ua: types.optional(types.maybeNull(types.string), ''),
		email: types.optional(types.string, ''),
		avatar_url: types.maybeNull(types.string),
		phone: types.maybeNull(types.string),
		link_to_deal: types.maybeNull(types.string),
		// @TODO: Change to references
		// locations: types.optional(types.array(types.reference(Location)), []),
		locations: types.optional(types.array(types.frozen()), []), // @TODO: Change to references
		services: types.optional(types.array(types.reference(Service)), []),
		studentParents: types.optional(types.array(Parent), []),
		students: types.optional(types.array(Student), []),
		// TODO change to Group Type
		groups: types.optional(types.array(types.frozen()), []),
		businesses: types.optional(types.array(BusinessUnit), []),
		chatJWT: types.maybe(types.string),
		admin: types.maybeNull(Admin),
		mentor: types.maybeNull(Mentor),
		teacher: types.maybeNull(Teacher),
		parent: types.maybeNull(Parent),
		student: types.maybeNull(Student),
		lang: types.maybeNull(types.string),
		logged_by: types.maybeNull(types.string),
		chatRulesAcceptances: types.maybe(types.map(types.boolean)),
		consultation: types.maybeNull(ConsultationProfileModel),
		is_completed_onboarding_quiz: types.maybeNull(types.boolean),
		is_available_consultation: types.optional(types.boolean, false),
		roles: types.optional(types.array(Role), []),
		referral_code: types.maybeNull(types.string),
		referrer_id: types.maybeNull(types.number),
		partner: types.maybeNull(Partner),
		cgCounselor: types.maybeNull(CgCounselor),
		cgCoordinator: types.maybeNull(ProfileCgCoordinator),
		system_tags: types.optional(types.array(types.string), []),
	})
	.preProcessSnapshot((snapshot: any) => ({
		...snapshot,
		services: (snapshot?.services ?? [])
			.slice()
			.sort((serviceA: number | { id?: number }, serviceB: number | { id?: number }) => {
				const serviceAId = typeof serviceA === 'number' ? serviceA : (serviceA?.id ?? 0);
				const serviceBId = typeof serviceB === 'number' ? serviceB : (serviceB?.id ?? 0);

				return serviceAId - serviceBId;
			}),
	}))
	.volatile(() => ({
		loading: false,
		imageUploadLoading: false,
	}))
	.views((self) => ({
		get isCreditsTisStudentView(): boolean {
			const tags = self.system_tags;
			const store = getParent(self) as IStoreInterfaceModel;

			return store.services.current.id === SERVICES.TIS
				&& !!checkRole('Student')
				&& [TAGS.TIS_CREDITS, TAGS.TRIAL_AI_TIS].some((tag) => tags.includes(tag));
		},
		get fullName() {
			return [self.first_name, self.last_name].filter(Boolean).join(' ');
		},
		get serviceLocations() {
			const store = getParent(self) as IStoreInterfaceModel;
			return self.locations.filter((location) => location.service_id === store.services.current.id);
		},
		get viewOnlyMode() {
			return (
				!!self.logged_by &&
				!['Super Admin', 'Admin', 'Teacher', 'Mentor/Tutor'].includes(self.logged_by)
			);
		},
		hasUserAction(actionType: UserActionTypes) {
			return self.actions.some((a) => a.action === actionType);
		},
	}))
	.actions((self) => ({
		checkRole(role: string) {
			return self.roles?.some((r) => r.name === role);
		},
		getRoleAvailability(role: string) {
			return self.roles?.some((r) => r.name === role) ? role : '';
		},
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getProfile();
				applySnapshot(self, result);
			} catch (e) {
				console.error(e);
			} finally {
				self.loading = false;
			}
		}),
		changeUserPassword: flow(function* (data: IChangeUserPassword) {
			self.loading = true;
			try {
				yield changeUserPassword(data);
			} finally {
				self.loading = false;
			}
		}),
		changeUserLanguage: flow(function* (lang: string) {
			const previousSnapshot = getSnapshot(self);
			try {
				self.loading = true;
				yield changeUserLanguage({
					default_lang: lang.toUpperCase(),
				});
				applySnapshot(self, {
					...previousSnapshot,
					lang,
				});
			} finally {
				self.loading = false;
			}
		}),
		uploadAvatar: flow(function* (file: File) {
			self.imageUploadLoading = true;
			const previousSnapshot = getSnapshot(self);

			let data: IUploadAvatarBody;

			try {
				if (file) {
					const mediaResponse = yield postMedia(file);

					if (mediaResponse?.url) {
						data = { avatar_url: mediaResponse.url };

						applySnapshot(self, {
							...previousSnapshot,
							avatar_url: mediaResponse.url,
						});
					}
				} else {
					data = { avatar_url: null };
				}

				const result = yield postAvatar(data);

				applySnapshot(self, { ...previousSnapshot, avatar_url: result.avatar_url });
			} catch (e) {
				applySnapshot(self, previousSnapshot);
				console.error(e, 'e');
				throw new Error(e);
			} finally {
				self.imageUploadLoading = false;
			}
		}),
		updateChatRuleSeenStatus: flow(function* (data: IReadChatRules) {
			const previousSnapshot = getSnapshot(self);
			try {
				yield readChatRules(data);
				applySnapshot(self, {
					...previousSnapshot,
					chatRulesAcceptances: {
						...previousSnapshot?.chatRulesAcceptances,
						[`${data.service_id}-${data.business_id}`]: true,
					},
				});
			} catch (e) {
				console.error(e, 'e');
				throw new Error(e);
			}
		}),
		commitUserAction: flow(function* ({ actionType }: { actionType: UserActionTypes }) {
			try {
				yield commitAction({
					actionType,
				});
				const result = yield getProfile();
				applySnapshot(self, result);
			} catch (e) {
				console.error(e);
			}
		}),
	}));

export default ProfileModel;

export interface IProfileModel extends Instance<typeof ProfileModel> {}
